// /home (byj-1wv.137): where a sign-in lands. What Share does, as cards in the
// guide's groups — each the place it opens and the guide's section on it —
// under a quick start of three commands, and above them what is mine now,
// counted from the lists the relay already serves: shares live, classes I
// teach and am in, hand-ins to grade, grades of mine released, files. A
// list that cannot be read is left out of it; the page stays.
import { useEffect, useState, type ReactNode } from "react";
import { api, type User } from "../api";
import { cardsFor, quickStart, serves, type HomeIcon } from "../home";
import { Link } from "../components/Nav";
import { Page, PageHead, PageLead } from "../components/Page";
import { AgentsIcon, BillingIcon, ClassesIcon, ExamsIcon, FilesIcon, MachinesIcon, RecordIcon, SharesIcon, TemplatesIcon, TerminalIcon } from "../components/PlaceIcons";
import { Steps } from "../components/Steps";
import { t } from "../i18n";

const icons: Record<HomeIcon, () => JSX.Element> = {
  shares: SharesIcon,
  record: RecordIcon,
  terminal: TerminalIcon,
  classes: ClassesIcon,
  exams: ExamsIcon,
  agents: AgentsIcon,
  templates: TemplatesIcon,
  files: FilesIcon,
  machines: MachinesIcon,
  billing: BillingIcon,
};

/** What is mine now, each a count with the page it is on; a count of none is not said. */
type Status = { key: string; text: string; to: string };

/** The lists the home page counts from, for tests. */
export type HomeLists = Pick<typeof api, "shares" | "classes" | "exams" | "files">;

function useStatus(user: User, lists: HomeLists): Status[] {
  const [status, setStatus] = useState<Status[]>([]);
  useEffect(() => {
    let live = true;
    const read = async () => {
      const [shares, classes, exams, files] = await Promise.allSettled([
        serves(user, "live") ? lists.shares() : Promise.reject(new Error("off")),
        serves(user, "classes") ? lists.classes() : Promise.reject(new Error("off")),
        serves(user, "exams") ? lists.exams() : Promise.reject(new Error("off")),
        serves(user, "files") ? lists.files() : Promise.reject(new Error("off")),
      ]);
      const out: Status[] = [];
      // each said by its own key, so the words a page asks for are found in it (i18n.test.ts)
      const say = (key: string, count: number, to: string, text: (count: number) => string) => count > 0 && out.push({ key, text: text(count), to });
      if (shares.status === "fulfilled") say("liveShares", shares.value.filter((s) => s.role === "owner" && s.live).length, "/shares", (count) => t("home:liveShares", { count }));
      if (classes.status === "fulfilled") {
        say("teaching", classes.value.filter((c) => c.role === "instructor").length, "/classes", (count) => t("home:teaching", { count }));
        say("taking", classes.value.filter((c) => c.role !== "instructor").length, "/classes", (count) => t("home:taking", { count }));
      }
      if (exams.status === "fulfilled") {
        say("toGrade", exams.value.teaching.reduce((n, e) => n + (e.queued ?? 0), 0), "/exams", (count) => t("home:toGrade", { count }));
        say("released", exams.value.assigned.reduce((n, a) => n + (a.released ?? 0), 0), "/exams", (count) => t("home:released", { count }));
      }
      if (files.status === "fulfilled") say("files", files.value.mine.length, "/files", (count) => t("home:files", { count }));
      if (live) setStatus(out);
    };
    void read();
    return () => {
      live = false;
    };
  }, [user, lists]);
  return status;
}

function Section({ id, title, children }: { id: string; title: string; children: ReactNode }) {
  return (
    <section className="home-section" aria-labelledby={`home-${id}`} data-testid={`home-${id}`}>
      <h2 id={`home-${id}`} className="rows-head">
        {title}
      </h2>
      {children}
    </section>
  );
}

export function Home({ user, lists = api }: { user: User; lists?: HomeLists }) {
  const status = useStatus(user, lists);
  const groups = cardsFor(user);
  return (
    <Page className="home">
      <PageHead title={t("home:title")} />
      <PageLead>{t("home:lead")}</PageLead>
      {status.length > 0 && (
        <Section id="status" title={t("home:statusTitle")}>
          <ul className="home-status">
            {status.map((s) => (
              <li key={s.key} data-testid={`status-${s.key}`}>
                <Link to={s.to} className="chip home-status-chip">
                  {s.text}
                </Link>
              </li>
            ))}
          </ul>
        </Section>
      )}
      {serves(user, "live") && (
        <Section id="quick" title={t("home:quickTitle")}>
          <p className="muted small">{t("home:quickLead")}</p>
          <Steps
            rail
            testid="home-steps"
            steps={[
              { title: t("home:stepInstall"), text: t("home:stepInstallText"), commands: quickStart.install },
              { title: t("home:stepSignIn"), text: t("home:stepSignInText"), commands: quickStart.signIn },
              { title: t("home:stepShare"), text: t("home:stepShareText"), commands: quickStart.share },
            ]}
          />
        </Section>
      )}
      {groups.map(({ group, cards }) => (
        <Section key={group} id={group} title={t(`home:group${group[0]!.toUpperCase()}${group.slice(1)}` as "home:groupSharing")}>
          <ul className="home-cards">
            {cards.map((c) => {
              const Icon = icons[c.icon];
              return (
                <li key={c.id} className="card home-card" data-testid={`card-${c.id}`}>
                  <div className="home-card-head">
                    <span className="home-card-icon">
                      <Icon />
                    </span>
                    <h3 className="home-card-title">{t(`home:cards.${c.id}.title`)}</h3>
                  </div>
                  <p className="home-card-text">{t(`home:cards.${c.id}.text`)}</p>
                  <div className="row wrap home-card-actions">
                    <Link to={c.to} className="button small">
                      {t(`home:cards.${c.id}.action`)}
                    </Link>
                    {c.doc && (
                      <Link to={`/docs#${c.doc}`} className="small">
                        {t("home:inGuide")}
                      </Link>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </Section>
      ))}
    </Page>
  );
}
